import { useLocalStorage, until } from '@vueuse/core'
import { defineStore } from 'pinia'
import Vue, { nextTick, computed } from 'vue'

import { useOnlineUserSessionStore } from '@/modules/account/onlineUserSessionStore'
import azureChatApi from '@/modules/chat/api/azureChat'
import { useAzureChatStore } from '@/modules/chat/azureChatStore'

// Add a cache for thread stores
const chatThreadStoreCache = {}

// Persist dismissed auto-translate prompts per chat thread (uses Azure thread id values)
const autoTranslatePromptDismissedThreadsRef = useLocalStorage('chatAutoTranslatePromptDismissedThreads', [])

function cloneMessage(message) {
  return {
    ...message,
    metadata: message.metadata ? { ...message.metadata } : undefined,
  }
}

function mergeMessagePatch(message, patch) {
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) {
    return message
  }
  const result = cloneMessage(message)

  Object.keys(patch).forEach((key) => {
    if (key === 'metadata') {
      const metadataPatch = patch.metadata

      if (metadataPatch && typeof metadataPatch === 'object' && !Array.isArray(metadataPatch)) {
        const metadata = { ...(result.metadata || {}) }
        Object.keys(metadataPatch).forEach((metadataKey) => {
          const value = metadataPatch[metadataKey]
          if (value === undefined) {
            delete metadata[metadataKey]
          } else {
            metadata[metadataKey] = value
          }
        })
        result.metadata = Object.keys(metadata).length ? metadata : undefined
      } else {
        result.metadata = metadataPatch
      }
      return
    }

    result[key] = patch[key]
  })

  return result
}

export const useChatThreadStore = (azureChatThreadId) => {
  if (!azureChatThreadId) {
    throw new Error('azureChatThreadId is required')
  }
  // Use cached instance if available
  if (!chatThreadStoreCache[azureChatThreadId]) {
    const store = defineStore(`chatThreadStore-${azureChatThreadId}`, {
      state: () => ({
        azureChatThreadId,
        // Messages that are shown in the UI
        messages: [],
        // Sorted messages that are shown in the UI
        sortedMessages: [],
        // All existing messages that have been loaded from Azure so far. Might contain messages not shown in the UI yet,
        // Because for some functionality we have to pre-load earlier messages already without showing them.
        // Some messages might not be in here, but in "messages", because new messages are not added here.
        allLoadedMessages: [],
        tempFilesMessages: {},
        chatThreadIsOpening: true,
        initialMessagesHaveBeenLoaded: false,
        allMessagesAreLoaded: false,
        sequenceIdOfFirstMessage: undefined,
        partnerReadReceipt: undefined,
        myReadReceipt: undefined,
        readReceiptsAreLoaded: false,
        messageIterator: undefined,
        typingIndicator: undefined,
        typingIndicatorTimeout: null,
        messagesScroller: null,
        unSeenMessages: 0,
        isScrollerAtBottom: true,
        resizeObserver: null,
        lastMessageId: null,
        showAutoTranslatePrompt: false,
        // Message being replied to (null when not replying)
        replyingToMessage: null,
        // Cache of replied-to messages, keyed by message ID
        repliedToMessages: {},
        // Track which messages have expanded reply previews (keyed by message ID)
        expandedReplyPreviews: {},
      }),
      getters: {
        // All known messages (cache + UI), deduped by id, sorted asc by id.
        // UI messages override cache entries if both exist.
        combinedMessages(state) {
          const byId = new Map()
          state.allLoadedMessages.forEach((m) => {
            if (m && m.id !== undefined && m.id !== null) {
              byId.set(m.id, m)
            }
          })
          state.messages.forEach((m) => {
            if (m && m.id !== undefined && m.id !== null) {
              byId.set(m.id, m)
            }
          })
          const merged = Array.from(byId.values())
          merged.sort((a, b) => a.id - b.id)
          return merged
        },
      },
      actions: {
        async initializeMessageIterator() {
          if (this.messageIterator) {
            return
          }
          const chatThreadClient = await azureChatApi.getChatThreadClient(this.azureChatThreadId)
          this.messageIterator = chatThreadClient.listMessages({ maxPageSize: 20 }).byPage()
        },
        async init(messagesScroller) {
          this.messagesScroller = messagesScroller
          this.allMessagesAreLoaded = false
          if (!this.initialMessagesHaveBeenLoaded) {
            this.chatThreadIsOpening = true
          }

          await this.initializeMessageIterator()

          // call loadMoreMessages only when a chat thread is opening first time
          if (!this.initialMessagesHaveBeenLoaded) {
            await this.loadMoreMessages()
          }

          if (!this.partnerReadReceipt || !this.myReadReceipt) {
            try {
              await this.loadReadReceipts()
            } catch (error) {
              console.error('Failed to load read receipts:', error)
            }
          }

          await this.scrollContainerDown()
        },
        updateSortedMessages() {
          this.sortedMessages = [...this.messages].filter((message) => message.content).sort((a, b) => {
            return a.id - b.id
          })
        },

        pushMessage(message) {
          const normalized = this.normalizeAzureMessage(message)
          if (!normalized) {
            return
          }
          const sequenceId = normalized._sequenceId
          // we don't want to fetch the newest messages here which were added after initial loading a chat thread.
          // These new messages are loaded using the "chatMessageReceived" event.
          if (!this.sequenceIdOfFirstMessage || sequenceId < this.sequenceIdOfFirstMessage) {
            this.messages.push(normalized)
          }
          if (!this.sequenceIdOfFirstMessage) {
            this.sequenceIdOfFirstMessage = sequenceId
          }
        },
        // Normalize an Azure message to the internal structure used by UI/cache.
        // Returns null for non-text messages.
        normalizeAzureMessage(azureMsg) {
          const sequenceId = parseInt(azureMsg.sequenceId)
          if (azureMsg.type !== 'text') {
            return null
          }
          return {
            id: parseInt(azureMsg.id),
            content: azureMsg.content?.message,
            senderDisplayName: azureMsg.senderDisplayName,
            createdOn: azureMsg.createdOn,
            metadata: azureMsg.metadata,
            _sequenceId: sequenceId,
          }
        },
        // Insert normalized messages into allLoadedMessages (dedup by id), keep sorted asc by id
        addToCache(normalizedMessages) {
          if (!normalizedMessages.length) {
            return
          }
          const existingIds = new Set(this.allLoadedMessages.map((message) => message.id))
          normalizedMessages.forEach((newMessage) => {
            if (!newMessage || existingIds.has(newMessage.id)) {
              return
            }
            this.allLoadedMessages.push(newMessage)
            existingIds.add(newMessage.id)
          })
          this.allLoadedMessages.sort((a, b) => a.id - b.id)
        },
        // Fetch a replied-to message by ID, with caching
        async fetchRepliedToMessage(messageId) {
          const messageIdStr = messageId.toString()
          // Check cache first
          if (this.repliedToMessages[messageIdStr]) {
            return this.repliedToMessages[messageIdStr]
          }
          // Check if in combinedMessages
          const existing = this.combinedMessages.find((message) => message.id === parseInt(messageId))
          if (existing) {
            // Clone to avoid mutating the shared object
            const clonedMessage = { ...existing }
            // Even if the message is deleted, azure still return it with metadata and content as null
            clonedMessage.deleted = !clonedMessage.metadata && !clonedMessage.content
            Vue.set(this.repliedToMessages, messageIdStr, clonedMessage)
            return clonedMessage
          }
          // Fetch from Azure
          try {
            const azureMessage = await azureChatApi.getMessage(this.azureChatThreadId, messageId)
            const normalized = this.normalizeAzureMessage(azureMessage)
            if (normalized) {
              normalized.deleted = false
              Vue.set(this.repliedToMessages, messageIdStr, normalized)
              return normalized
            }
            // normalizeAzureMessage returned null
            const deletedMessage = { deleted: true }
            Vue.set(this.repliedToMessages, messageIdStr, deletedMessage)
            return deletedMessage
          } catch (error) {
            // Message was deleted or not found
            Vue.set(this.repliedToMessages, messageIdStr, { deleted: true })
            return { deleted: true }
          }
        },
        // Base loader: fetch older text messages into cache only, up to maxCount
        async fetchOlderMessagesIntoCache(maxCount = 20) {
          await this.initializeMessageIterator()
          let remaining = maxCount
          const newlyCached = []
          while (remaining > 0) {
            // eslint-disable-next-line no-await-in-loop
            const { done, value: page } = await this.messageIterator.next()
            if (done) {
              this.allMessagesAreLoaded = true
              break
            }
            for (const azureMsg of page) {
              const norm = this.normalizeAzureMessage(azureMsg)
              if (norm) {
                newlyCached.push(norm)
                if (remaining > 0) {
                  remaining -= 1
                }
              }
            }
          }
          this.addToCache(newlyCached)
          // Ensure initial flag is set once we've fetched at least one
          if (!this.initialMessagesHaveBeenLoaded && this.allLoadedMessages.length) {
            this.initialMessagesHaveBeenLoaded = true
          }
          return newlyCached
        },
        // Try to collect up to maxCount cached messages older than beforeId that are not already in UI
        collectFromCacheBeforeId(beforeId, maxCount) {
          const uiMessageIds = new Set(this.messages.map((message) => message.id))
          const candidates = this.allLoadedMessages.filter((message) => {
            if (message.id >= beforeId) {
              return false
            }
            return !uiMessageIds.has(message.id)
          })
          if (!candidates.length) {
            return []
          }
          // Take the latest maxCount messages that come after beforeId
          // We take from the end of the array, as the messages are sorted by id ascending
          const slice = candidates.slice(Math.max(0, candidates.length - maxCount))
          return slice
        },
        // Add more older messages to the UI. Loads more messages from from azure if necessary
        async loadMoreMessages({ maxCount = 20 } = {}) {
          const uiOldestId = this.sortedMessages[0]?.id ?? Number.POSITIVE_INFINITY
          let toAttach = this.collectFromCacheBeforeId(uiOldestId, maxCount)
          if (toAttach.length < maxCount && !this.allMessagesAreLoaded) {
            const messagesToFetch = maxCount - toAttach.length
            await this.fetchOlderMessagesIntoCache(messagesToFetch)
            const more = this.collectFromCacheBeforeId(uiOldestId, messagesToFetch)
            toAttach = [...toAttach, ...more]
          }
          if (toAttach.length) {
            const existingIds = new Set(this.messages.map((message) => message.id))
            toAttach.forEach((message) => {
              if (!existingIds.has(message.id)) {
                this.messages.push({
                  id: message.id,
                  content: message.content,
                  senderDisplayName: message.senderDisplayName,
                  createdOn: message.createdOn,
                  metadata: message.metadata,
                })
                existingIds.add(message.id)
              }
            })
            await this.updateSortedMessages()
          }
        },
        async getLastMessage() {
          if (!this.sortedMessages.length) {
            // fetch the last message if there aren't already messages stored
            const chatThreadClient = await azureChatApi.getChatThreadClient(this.azureChatThreadId)
            const lastMessageIterator = await chatThreadClient.listMessages({ maxPageSize: 1 }).byPage()
            const { done, value: page } = await lastMessageIterator.next()

            if (done || !page.length) {
              return null
            }

            await this.pushMessage(page[0])
            await this.updateSortedMessages()
          }

          if (this.sortedMessages.length) {
            const lastMessage = this.sortedMessages[this.sortedMessages.length - 1]
            return {
              azure_chat_thread_id: this.azureChatThreadId,
              azure_message_id: lastMessage.id,
              message: lastMessage.content,
              metadata: lastMessage.metadata,
              azure_sender_id: lastMessage.senderDisplayName,
              created_at: lastMessage.createdOn,
            }
          }

          return null
        },
        async loadReadReceipts(force = false) {
          // read receipts are loaded once. Then, they are updated by web sockets
          if (!force && this.readReceiptsAreLoaded) {
            return
          }
          const azureChatStore = useAzureChatStore()
          const azureReadReceipts = await azureChatApi.getReadReceipts(this.azureChatThreadId)

          this.readReceiptsAreLoaded = true

          if (!azureReadReceipts.length) {
            return
          }

          this.partnerReadReceipt = azureReadReceipts.find((readReceipt) => {
            return readReceipt.sender.communicationUserId !== azureChatStore.myIdentity
          })
          this.myReadReceipt = azureReadReceipts.find((readReceipt) => {
            return readReceipt.sender.communicationUserId === azureChatStore.myIdentity
          })
          this.lastMessageId = this.myReadReceipt?.chatMessageId
        },
        async setReadReceipt(newMessageId) {
          const onlineUserSessionStore = useOnlineUserSessionStore()
          // To not have any extra calls and we already set the read receipt
          if (!newMessageId || this.lastMessageId === newMessageId || !onlineUserSessionStore.isOnline) {
            return
          }

          this.lastMessageId = newMessageId
          await azureChatApi.setReadingEvent(this.azureChatThreadId, newMessageId)
          // There can be a race condition when we try to update the read receipt before we've loaded the current one.
          try {
            await until(computed(() => this.myReadReceipt), { timeout: 5000, throwOnTimeout: true }).toBeTruthy()
          } catch (e) {
            await this.loadReadReceipts(true)
          }
          if (this.myReadReceipt) {
            this.myReadReceipt.chatMessageId = newMessageId
            this.myReadReceipt.readOn = new Date()
          }
        },
        scrollContainerDown() {
          if (this.messagesScroller) {
            this.messagesScroller.scrollToBottom()
          }
        },
        observeResize() {
          if (this.resizeObserver) {
            this.resizeObserver.disconnect()
          }

          this.resizeObserver = new ResizeObserver(async () => {
            await nextTick()
            if (this.isScrollerAtBottom) {
              setTimeout(() => {
                this.scrollContainerDown()
              }, 500)
            }
          })
          if (this.messagesScroller?.$el) {
            this.resizeObserver.observe(this.messagesScroller.$el.querySelector('.vue-recycle-scroller__item-wrapper'))
          }
        },
        reset() {
          if (this.typingIndicatorTimeout) {
            clearTimeout(this.typingIndicatorTimeout)
            this.typingIndicatorTimeout = null
          }

          this.messages = []
          this.sortedMessages = []
          this.allLoadedMessages = []
          this.tempFilesMessages = {}
          this.chatThreadIsOpening = true
          this.initialMessagesHaveBeenLoaded = false
          this.allMessagesAreLoaded = false
          this.sequenceIdOfFirstMessage = undefined
          this.partnerReadReceipt = undefined
          this.myReadReceipt = undefined
          this.readReceiptsAreLoaded = false
          this.messageIterator = undefined
          this.typingIndicator = undefined
          this.unSeenMessages = 0
          this.lastMessageId = null
          this.showAutoTranslatePrompt = false
          this.replyingToMessage = null
          this.repliedToMessages = {}
          this.expandedReplyPreviews = {}
        },
        updateMessageByAzureId(messageId, patch) {
          return this.updateMessageInternal((message) => {
            return Number(message.id) === Number(messageId)
          }, patch)
        },
        updateMessageByTempId(tempId, patch) {
          if (tempId === undefined || tempId === null) {
            return null
          }

          const tempIdAsString = tempId.toString()
          return this.updateMessageInternal((message) => {
            return message.metadata?.messageId && message.metadata.messageId.toString() === tempIdAsString
          }, patch)
        },
        updateMessageInternal(predicate, patch) {
          const index = this.messages.findIndex(predicate)

          if (index === -1) {
            return null
          }

          const currentMessage = this.messages[index]
          const updatedMessage = mergeMessagePatch(currentMessage, patch)

          if (!updatedMessage) {
            return null
          }

          this.messages.splice(index, 1, updatedMessage)
          this.updateSortedMessages()

          return updatedMessage
        },
        triggerAutoTranslatePromptForThread() {
          if (autoTranslatePromptDismissedThreadsRef.value.includes(this.azureChatThreadId)) {
            return
          }
          this.showAutoTranslatePrompt = true
        },
        hideAutoTranslatePrompt() {
          const threadId = this.azureChatThreadId
          if (!autoTranslatePromptDismissedThreadsRef.value.includes(threadId)) {
            autoTranslatePromptDismissedThreadsRef.value.push(threadId)
          }
          this.showAutoTranslatePrompt = false
        },
      },
    })
    chatThreadStoreCache[azureChatThreadId] = store()
  }
  return chatThreadStoreCache[azureChatThreadId]
}
